import React, { useState, useEffect } from 'react';
import axios from 'axios';

const getApiUrl = () => {
  if (window.location.hostname !== 'localhost') return window.location.origin;
  return window.location.port === '3001' ? 'http://localhost:3000' : 'http://localhost:3000';
};
const API_URL = getApiUrl();

const channels = [
  { id: '922986590894796',  name: '🏢 Comercial' },
  { id: '999551239899325',  name: '👔 Administrativo' },
  { id: '916595048209652',  name: '🌎 R. Internacionales' },
  { id: '1020657397786989', name: '📈 Becarios' },
  { id: '983548531506198',  name: '⭐ Regulares' },
  { id: '1308381959018456', name: '🇵🇪 Peru' },
  { id: '1000790306449643', name: 'ℹ️ Info 2' },
  { id: '1233806963139486', name: '🎓 Académico' },
  { id: '1232012809998778', name: '📦 Logística' },
  { id: '1348031501732308', name: '🤝 ONG' },
];

function BroadcastFromSheetModal({ onSelect, onClose, theme = 'light', isBroadcasting = false, broadcastProgress = null, onCancelBroadcast }) {
  const [step, setStep] = useState(1);
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState(null);
  const [sheetPreview, setSheetPreview] = useState(null);
  const [sheetPhoneCol, setSheetPhoneCol] = useState(0);
  const [sheetNameCol, setSheetNameCol] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paramValues, setParamValues] = useState({});
  const [selectedChannel, setSelectedChannel] = useState('');
  const [templateChannelFilter, setTemplateChannelFilter] = useState('all');
  const [forceResend, setForceResend] = useState(false);

  const isDark = theme === 'dark';
  const columnLetter = (i) => String.fromCharCode(65 + (i % 26));
  const filteredTemplates = templates.filter(t => {
    const term = searchTerm.toLowerCase();
    const bodyText = (t.components?.find(c => c.type === 'BODY')?.text || '').toLowerCase();
    const matchesSearch = t.name.toLowerCase().includes(term) || bodyText.includes(term);
    const matchesChannel = templateChannelFilter === 'all' ||
      t.channel_id === templateChannelFilter ||
      (templateChannelFilter === '1348031501732308' && (t.channel_name === 'ONG' || t.channel_id === '1348031501732308'));
    return matchesSearch && matchesChannel;
  });

  useEffect(() => {
    const f = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/templates`);
        setTemplates(res.data.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingTemplates(false);
      }
    };
    f();
  }, []);

  const handlePreview = async () => {
    const url = sheetUrl.trim();
    if (!url) return;
    setSheetLoading(true);
    setSheetError(null);
    setSheetPreview(null);
    try {
      const res = await axios.post(`${API_URL}/api/broadcast/import-from-sheet`, { url, preview: true }, { headers: { 'Content-Type': 'application/json' } });
      const { previewRows, totalRows, columnCount } = res.data || {};
      if (!Array.isArray(previewRows) || previewRows.length === 0) {
        setSheetError('La hoja está vacía o no se pudo leer.');
        return;
      }
      const cols = Math.max(columnCount || 0, previewRows[0]?.length || 0, 2);
      setSheetPreview({ previewRows, totalRows: totalRows || previewRows.length, columnCount: cols });
      setSheetPhoneCol(0);
      setSheetNameCol(cols > 1 ? 1 : 0);
    } catch (err) {
      setSheetError(err.response?.data?.error || err.message || 'Error al cargar la hoja');
    } finally {
      setSheetLoading(false);
    }
  };

  const handleConfirmContacts = async () => {
    const url = sheetUrl.trim();
    if (!url) return;
    setSheetLoading(true);
    setSheetError(null);
    try {
      const res = await axios.post(`${API_URL}/api/broadcast/import-from-sheet`, {
        url,
        phoneColumnIndex: sheetPhoneCol,
        nameColumnIndex: sheetNameCol
      }, { headers: { 'Content-Type': 'application/json' } });
      const { contacts } = res.data || {};
      if (!Array.isArray(contacts) || contacts.length === 0) {
        setSheetError('No se encontraron contactos con teléfono válido.');
        return;
      }
      setRecipients(contacts.map((c, i) => ({
        id: `sheet_${Date.now()}_${i}`,
        wa_id: c.wa_id,
        name: c.name || 'Desconocido',
        isTemp: true
      })));
      setStep(2);
    } catch (err) {
      setSheetError(err.response?.data?.error || err.message || 'Error al cargar contactos');
    } finally {
      setSheetLoading(false);
    }
  };

  const goToConfirm = () => {
    if (selectedTemplate && recipients.length > 0) setStep(3);
  };

  const handleSend = () => {
    const components = [];

    // 1. Header Component
    const header = selectedTemplate?.components?.find(c => c.type === 'HEADER');
    if (header) {
      if (header.format === 'TEXT' && header.text.includes('{{1}}')) {
        components.push({
          type: 'header',
          parameters: [{ type: 'text', text: paramValues['header_1'] || '' }]
        });
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
        const url = paramValues['header_url'];
        if (url) {
          const mediaType = header.format.toLowerCase();
          components.push({
            type: 'header',
            parameters: [{ type: mediaType, [mediaType]: { link: url } }]
          });
        }
      }
    }

    // 2. Body Component
    const body = selectedTemplate?.components?.find(c => c.type === 'BODY');
    const text = body?.text || '';
    const matches = [...text.matchAll(/{{(\d+)}}/g)];

    if (matches.length > 0) {
      const params = [...new Set(matches.map(m => m[1]))].sort((a, b) => parseInt(a) - parseInt(b));
      const bodyParams = params.map(p => ({
        type: 'text',
        text: paramValues[p] || ''
      }));
      components.push({ type: 'body', parameters: bodyParams });
    }

    const payload = recipients.map(r => ({ id: null, wa_id: r.wa_id, name: r.name }));
    onSelect(selectedTemplate, payload, components, selectedChannel || undefined, forceResend);
  };

  return (
    <div className={`modal-overlay broadcast-sheet-modal ${isDark ? 'broadcast-modal--dark' : ''}`} onClick={onClose}>
      <div className="broadcast-sheet-modal-content" onClick={e => e.stopPropagation()}>
        <div className="broadcast-sheet-modal-header" style={{ background: '#00a884', color: '#fff', padding: '16px 20px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>Envío masivo desde Google Sheets</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', opacity: 0.9 }}>&times;</button>
        </div>

        <div className="broadcast-sheet-modal-body" style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Stepper */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: isDark ? '1px solid #2a3942' : '1px solid #e9edef', paddingBottom: '12px' }}>
            <span style={{ fontWeight: step >= 1 ? 600 : 400, color: step >= 1 ? '#00a884' : (isDark ? '#8696a0' : '#54656f') }}>1. Hoja</span>
            <span style={{ color: isDark ? '#2a3942' : '#ccc' }}>→</span>
            <span style={{ fontWeight: step >= 2 ? 600 : 400, color: step >= 2 ? '#00a884' : (isDark ? '#8696a0' : '#54656f') }}>2. Plantilla</span>
            <span style={{ color: isDark ? '#2a3942' : '#ccc' }}>→</span>
            <span style={{ fontWeight: step >= 3 ? 600 : 400, color: step >= 3 ? '#00a884' : (isDark ? '#8696a0' : '#54656f') }}>3. Enviar</span>
          </div>

          {step === 1 && (
            <div className="broadcast-sheet-step1">
              {!sheetPreview ? (
                <>
                  <p style={{ margin: '0 0 14px', fontSize: '14px', color: isDark ? '#aebac1' : '#54656f' }}>
                    Pega el enlace de la hoja (Archivo → Compartir → Publicar en la web, CSV). Elegirás qué columna es teléfono y nombre; luego pasas directo a elegir plantilla y enviar.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <input
                      type="url"
                      value={sheetUrl}
                      onChange={e => { setSheetUrl(e.target.value); setSheetError(null); }}
                      onKeyDown={e => e.key === 'Enter' && handlePreview()}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: isDark ? '1px solid #2a3942' : '1px solid #e9edef',
                        background: isDark ? '#111b21' : '#fff',
                        color: isDark ? '#e9edef' : '#111b21'
                      }}
                    />
                    <button type="button" onClick={handlePreview} disabled={!sheetUrl.trim() || sheetLoading} style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#00a884', color: '#fff', fontWeight: 600, cursor: sheetLoading ? 'wait' : 'pointer' }}>
                      {sheetLoading ? 'Cargando…' : 'Vista previa'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '14px', color: isDark ? '#aebac1' : '#54656f' }}>{sheetPreview.totalRows} filas</span>
                    <button type="button" onClick={() => { setSheetPreview(null); setSheetError(null); }} style={{ background: 'none', border: 'none', color: '#00a884', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}>Cambiar URL</button>
                  </div>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: isDark ? '#e9edef' : '#111b21' }}>
                      Columna <strong>teléfono</strong>
                      <select value={sheetPhoneCol} onChange={e => setSheetPhoneCol(Number(e.target.value))} style={{ padding: '6px 10px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', background: isDark ? '#111b21' : '#fff', color: 'inherit', minWidth: '100px' }}>
                        {Array.from({ length: sheetPreview.columnCount }, (_, i) => <option key={i} value={i}>Col {columnLetter(i)}</option>)}
                      </select>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: isDark ? '#e9edef' : '#111b21' }}>
                      Columna <strong>nombre</strong>
                      <select value={sheetNameCol} onChange={e => setSheetNameCol(Number(e.target.value))} style={{ padding: '6px 10px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', background: isDark ? '#111b21' : '#fff', color: 'inherit', minWidth: '100px' }}>
                        {Array.from({ length: sheetPreview.columnCount }, (_, i) => <option key={i} value={i}>Col {columnLetter(i)}</option>)}
                      </select>
                    </label>
                  </div>
                  <div style={{ overflowX: 'auto', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', borderRadius: '10px', marginBottom: '14px', background: isDark ? '#202c33' : '#fff' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          {Array.from({ length: sheetPreview.columnCount }, (_, i) => (
                            <th key={i} style={{ padding: '8px 10px', textAlign: 'left', background: isDark ? '#2a3942' : '#f5f6f6', color: isDark ? '#aebac1' : '#54656f', borderBottom: isDark ? '1px solid #2a3942' : '1px solid #e9edef' }}>
                              {columnLetter(i)}{sheetPhoneCol === i ? ' (tel)' : sheetNameCol === i ? ' (nombre)' : ''}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sheetPreview.previewRows.slice(0, 8).map((row, ri) => (
                          <tr key={ri}>
                            {Array.from({ length: sheetPreview.columnCount }, (_, ci) => (
                              <td key={ci} style={{ padding: '8px 10px', borderBottom: isDark ? '1px solid #2a3942' : '1px solid #f0f2f5', color: isDark ? '#e9edef' : '#111b21' }}>
                                {(row[ci] != null ? String(row[ci]) : '').slice(0, 35)}{(row[ci] != null && String(row[ci]).length > 35) ? '…' : ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={handleConfirmContacts} disabled={sheetLoading} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#00a884', color: '#fff', fontWeight: 600, cursor: sheetLoading ? 'wait' : 'pointer' }}>
                    {sheetLoading ? 'Cargando…' : `Usar ${sheetPreview.totalRows} contacto(s) y elegir plantilla`}
                  </button>
                </>
              )}
              {sheetError && <div style={{ marginTop: '12px', padding: '10px', background: isDark ? 'rgba(185,28,28,0.25)' : '#fef2f2', color: isDark ? '#fca5a5' : '#b91c1c', borderRadius: '8px', fontSize: '13px' }}>{sheetError}</div>}
            </div>
          )}

          {step === 2 && (
            <div className="broadcast-sheet-step2">
              <p style={{ margin: '0 0 12px', fontSize: '14px', color: isDark ? '#aebac1' : '#54656f' }}>
                {recipients.length} contactos desde la hoja. Elige la plantilla de WhatsApp.
              </p>
              <input
                type="text"
                placeholder="Buscar plantilla por nombre o texto..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', marginBottom: '10px', background: isDark ? '#111b21' : '#fff', color: 'inherit' }}
              />

              {/* Filtro rápido por canal */}
              <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '12px' }}>
                <button
                  type="button"
                  onClick={() => setTemplateChannelFilter('all')}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '16px',
                    border: '1px solid ' + (templateChannelFilter === 'all' ? '#00a884' : (isDark ? '#3b4a54' : '#e9edef')),
                    background: templateChannelFilter === 'all' ? '#00a884' : (isDark ? '#2a3942' : '#f0f2f5'),
                    color: templateChannelFilter === 'all' ? '#fff' : (isDark ? '#e9edef' : '#54656f'),
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Todos ({templates.length})
                </button>
                {channels.map(ch => {
                  const count = templates.filter(t => t.channel_id === ch.id || (ch.id === '1348031501732308' && t.channel_name === 'ONG')).length;
                  const isActive = templateChannelFilter === ch.id;
                  const isOng = ch.id === '1348031501732308';
                  return (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => setTemplateChannelFilter(ch.id)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '16px',
                        border: '1px solid ' + (isActive ? (isOng ? '#10B981' : '#00a884') : (isDark ? '#3b4a54' : '#e9edef')),
                        background: isActive ? (isOng ? '#10B981' : '#00a884') : (isDark ? '#2a3942' : '#f0f2f5'),
                        color: isActive ? '#fff' : (isDark ? '#e9edef' : '#54656f'),
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {ch.name} ({count})
                    </button>
                  );
                })}
              </div>

              {loadingTemplates ? (
                <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#8696a0' : '#666' }}>Cargando plantillas...</div>
              ) : filteredTemplates.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#8696a0' : '#666' }}>No hay plantillas{templateChannelFilter !== 'all' ? ' para este canal' : ''}.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {filteredTemplates.map(t => {
                    const headerImg = t.components?.find(c => c.type === 'HEADER' && c.format === 'IMAGE')?.example?.header_handle?.[0];
                    const isOng = t.channel_name === 'ONG' || t.channel_id === '1348031501732308';
                    const isSelected = selectedTemplate?.id === t.id;
                    return (
                      <div
                        key={t.id + '_' + (t.language || 'es') + '_' + (t.channel_id || '')}
                        onClick={() => {
                          setSelectedTemplate(t);
                          if (t.channel_id) setSelectedChannel(t.channel_id);
                          if (headerImg) {
                            setParamValues(prev => ({ ...prev, header_url: headerImg }));
                          }
                        }}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: isSelected ? '2px solid #00a884' : (isDark ? '1px solid #2a3942' : '1px solid #e9edef'),
                          background: isSelected ? (isDark ? 'rgba(0,168,132,0.2)' : '#e8f5e9') : (isDark ? '#202c33' : '#fff'),
                          cursor: 'pointer',
                          color: isDark ? '#e9edef' : '#111b21'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>{t.name}</span>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: isDark ? '#2a3942' : '#f0f2f5', color: isDark ? '#aebac1' : '#54656f' }}>
                              🌐 {t.language}
                            </span>
                            {t.channel_name && (
                              <span style={{
                                fontSize: '11px',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontWeight: 600,
                                backgroundColor: isOng ? '#10B981' : '#00a884',
                                color: '#fff'
                              }}>
                                {isOng ? '🤝 ONG' : t.channel_name}
                              </span>
                            )}
                          </div>
                        </div>
                        {headerImg && (
                          <div style={{ marginTop: '6px', marginBottom: '4px' }}>
                            <img src={headerImg} alt="" style={{ maxHeight: '70px', borderRadius: '6px', objectFit: 'cover' }} />
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: isDark ? '#8696a0' : '#54656f', marginTop: '4px' }}>
                          {(t.components?.find(c => c.type === 'BODY')?.text || '').slice(0, 100)}{(t.components?.find(c => c.type === 'BODY')?.text || '').length > 100 ? '…' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setStep(1)} style={{ padding: '10px 18px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>Atrás</button>
                <button type="button" onClick={goToConfirm} disabled={!selectedTemplate} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: selectedTemplate ? '#00a884' : (isDark ? '#2a3942' : '#ccc'), color: '#fff', fontWeight: 600, cursor: selectedTemplate ? 'pointer' : 'not-allowed' }}>Siguiente</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="broadcast-sheet-step3">
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>🚀</div>
                <h4 style={{ margin: '0 0 8px', color: isDark ? '#e9edef' : '#111b21' }}>Listo para enviar</h4>
                <p style={{ margin: 0, fontSize: '14px', color: isDark ? '#8696a0' : '#54656f' }}>{recipients.length} contactos · Plantilla: {selectedTemplate?.name}</p>
              </div>
              <div style={{ background: isDark ? '#2a3942' : '#f0f2f5', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: isDark ? '#aebac1' : '#54656f' }}>Destinatarios</span>
                  <span style={{ fontWeight: 600, color: '#00a884' }}>{recipients.length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: isDark ? '#aebac1' : '#54656f' }}>Plantilla</span>
                  <span style={{ color: isDark ? '#e9edef' : '#111b21' }}>{selectedTemplate?.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: isDark ? '#aebac1' : '#54656f' }}>Canal</span>
                  <select value={selectedChannel} onChange={e => setSelectedChannel(e.target.value)} style={{ padding: '6px 10px', borderRadius: '6px', border: isDark ? '1px solid #3b4a54' : '1px solid #d1d7db', background: isDark ? '#111b21' : '#fff', color: 'inherit', maxWidth: '200px' }}>
                    <option value="">Automático</option>
                    {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
                  </select>
                </div>
                {/* Force Resend */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={forceResend}
                      onChange={e => setForceResend(e.target.checked)}
                      style={{ width: '16px', height: '16px', accentColor: '#f59e0b', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px', color: isDark ? '#fbbf24' : '#92400e', fontWeight: 500 }}>
                      ⚡ Forzar reenvío (ignorar filtro anti-duplicado)
                    </span>
                  </label>
                </div>
              </div>

              {/* Header Parameters */}
              {(() => {
                const header = selectedTemplate?.components?.find(c => c.type === 'HEADER');
                if (!header) return null;

                if (header.format === 'TEXT' && header.text.includes('{{1}}')) {
                  return (
                    <div style={{ marginBottom: '16px', padding: '10px', background: isDark ? '#202c33' : '#f0f2f5', borderRadius: '8px' }}>
                      <h5 style={{ margin: '0 0 8px', fontSize: '14px', color: isDark ? '#aebac1' : '#54656f' }}>Cabecera (Variable)</h5>
                      <label style={{ display: 'block', fontSize: '12px', color: isDark ? '#8696a0' : '#666', marginBottom: '4px' }}>Valor para {'{{1}}'}</label>
                      <input
                        type="text"
                        value={paramValues['header_1'] || ''}
                        onChange={e => setParamValues(prev => ({ ...prev, header_1: e.target.value }))}
                        placeholder="Ej: Hola"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', background: isDark ? '#111b21' : '#fff', color: 'inherit' }}
                      />
                    </div>
                  );
                } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)) {
                  return (
                    <div style={{ marginBottom: '16px', padding: '10px', background: isDark ? '#202c33' : '#f0f2f5', borderRadius: '8px' }}>
                      <h5 style={{ margin: '0 0 8px', fontSize: '14px', color: isDark ? '#aebac1' : '#54656f' }}>Cabecera Multimedia ({header.format})</h5>
                      <label style={{ display: 'block', fontSize: '12px', color: isDark ? '#8696a0' : '#666', marginBottom: '4px' }}>URL del archivo (https://...)</label>
                      <input
                        type="url"
                        value={paramValues['header_url'] || ''}
                        onChange={e => setParamValues(prev => ({ ...prev, header_url: e.target.value }))}
                        placeholder="https://ejemplo.com/archivo.jpg"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', background: isDark ? '#111b21' : '#fff', color: 'inherit' }}
                      />
                    </div>
                  );
                }
                return null;
              })()}

              {/* Body Parameters */}
              {(() => {
                const body = selectedTemplate?.components?.find(c => c.type === 'BODY');
                const text = body?.text || '';
                const matches = [...text.matchAll(/{{(\d+)}}/g)];
                if (matches.length === 0) return null;
                const params = [...new Set(matches.map(m => m[1]))].sort((a, b) => parseInt(a) - parseInt(b));
                return (
                  <div style={{ marginBottom: '16px' }}>
                    <h5 style={{ margin: '0 0 8px', fontSize: '14px', color: isDark ? '#aebac1' : '#54656f' }}>Variables del Mensaje</h5>
                    {params.map(p => (
                      <div key={p} style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: isDark ? '#8696a0' : '#666', marginBottom: '4px' }}>Variable {`{{${p}}}`}</label>
                        <input
                          type="text"
                          value={paramValues[p] || ''}
                          onChange={e => setParamValues(prev => ({ ...prev, [p]: e.target.value }))}
                          placeholder={`Valor para {{${p}}}`}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', background: isDark ? '#111b21' : '#fff', color: 'inherit' }}
                        />
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" onClick={() => setStep(2)} disabled={isBroadcasting} style={{ padding: '10px 18px', borderRadius: '8px', border: isDark ? '1px solid #2a3942' : '1px solid #e9edef', background: 'transparent', color: 'inherit', cursor: isBroadcasting ? 'not-allowed' : 'pointer' }}>Atrás</button>
                <button
                  type="button"
                  onClick={isBroadcasting ? undefined : handleSend}
                  disabled={isBroadcasting}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: isBroadcasting ? '#6b9e90' : '#00a884', color: '#fff', fontWeight: 600, cursor: isBroadcasting ? 'not-allowed' : 'pointer', opacity: isBroadcasting ? 0.8 : 1 }}
                >
                  {isBroadcasting ? '⏳ Enviando...' : 'Enviar difusión'}
                </button>
                {isBroadcasting && onCancelBroadcast && (
                  <button
                    type="button"
                    onClick={onCancelBroadcast}
                    style={{ padding: '10px 18px', borderRadius: '8px', border: '2px solid #ef4444', background: 'transparent', color: '#ef4444', fontWeight: 600, cursor: 'pointer' }}
                  >
                    ✕ Cancelar
                  </button>
                )}
              </div>
              {isBroadcasting && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '13px', color: isDark ? '#aebac1' : '#54656f', marginBottom: '6px', textAlign: 'center' }}>
                    Enviando mensaje único... por favor espera.
                  </div>
                  <div style={{ height: '6px', background: isDark ? '#2a3942' : '#e9edef', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: '60%',
                      background: '#00a884',
                      borderRadius: '10px',
                      animation: 'bcast-pulse 1.5s ease-in-out infinite'
                    }} />
                  </div>
                  <style>{`
                    @keyframes bcast-pulse {
                      0% { width: 10%; }
                      50% { width: 85%; }
                      100% { width: 10%; }
                    }
                  `}</style>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <style>{`
        .broadcast-sheet-modal.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .broadcast-sheet-modal .broadcast-sheet-modal-content { max-width: 520px; width: 92%; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 12px 40px rgba(0,0,0,0.25); }
        .broadcast-sheet-modal.broadcast-modal--dark .broadcast-sheet-modal-content { background: #111b21; }
      `}</style>
    </div>
  );
}

export default BroadcastFromSheetModal;
